/**
 * Listen buttons placed in the page: the site puts
 *
 *   <div data-readingvox-button></div>
 *
 * where it wants one (a footer, under the title) and the widget fills it with a
 * "Listen to this page" button that reads the whole page and shows its state
 * (loading, pause with progress, resume). Options on the placeholder:
 *   data-label="…"         own wording for the idle button
 *   data-size="small"      a smaller button
 *   data-toolbar="true"    add a ⋯ that opens the full toolbar
 *   data-action="toolbar"  open the toolbar instead of reading the page
 *
 * Every placeholder drives the same player. Placeholders added later (React,
 * single-page apps) are picked up. Rendered in a shadow root so the site's CSS
 * can't restyle it.
 */
import type { ResolvedTheme } from './ThemeManager';
import { t } from '../i18n';

export type PageListenState = 'idle' | 'loading' | 'playing' | 'paused';

export interface ListenButtonHost {
  /** Read the page from the start, or resume it if paused */
  playPage(): Promise<void>;
  pause(): void;
  pageState(): { state: PageListenState; progress: number };
  /** Open the reading toolbar; absent when the mode has none */
  openToolbar?: () => void;
}

const SELECTOR = '[data-readingvox-button]';
const POLL_MS = 250;
/** Give up showing "Loading…" if nothing starts by then */
const PENDING_MS = 15000;

const LOGO = `<svg class="logo" viewBox="26 57 167 109" aria-hidden="true" focusable="false"> <rect x="31" y="90" width="26" height="45" rx="13" fill="#2ec486"/> <rect x="63" y="65" width="26" height="95" rx="13" fill="#8d99ae"/> <rect x="96" y="82" width="26" height="61" rx="13" fill="#2ec486"/> <rect x="129" y="65" width="26" height="95" rx="13" fill="#ff9f1c"/> <rect x="161" y="90" width="26" height="45" rx="13" fill="#8d99ae"/> </svg>`;
const PAUSE = `<svg class="glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>`;
const PLAY = `<svg class="glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z"/></svg>`;
const SPINNER = `<span class="spinner" aria-hidden="true"></span>`;
const MORE = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>`;

function css(theme: ResolvedTheme): string {
  return ` :host { display: inline-block; vertical-align: middle; line-height: normal; } .wrap { display: inline-flex; align-items: stretch; position: relative; border-radius: 8px; border: 1px solid ${theme.borderColor}; background: ${theme.backgroundColor}; color: ${theme.textColor}; box-shadow: 0 1px 2px rgba(0,0,0,0.06); overflow: hidden; font: 600 14px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; } .wrap:hover { border-color: ${theme.primaryColor}; } button { all: unset; box-sizing: border-box; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; height: 40px; padding: 0 16px 0 12px; color: inherit; font: inherit; white-space: nowrap; } button:focus-visible { outline: 3px solid #2563eb; outline-offset: -3px; } button:hover { background: ${theme.primaryColor}14; } .more { padding: 0 10px; border-left: 1px solid ${theme.borderColor}; } .more svg { width: 18px; height: 18px; fill: currentColor; opacity: 0.75; } .icon { display: inline-flex; width: 22px; height: 22px; align-items: center; justify-content: center; flex-shrink: 0; } .logo { width: 22px; height: 22px; } .glyph { width: 18px; height: 18px; fill: ${theme.primaryColor}; } .spinner { width: 16px; height: 16px; border-radius: 50%; border: 2px solid ${theme.primaryColor}33; border-top-color: ${theme.primaryColor}; animation: spin 0.8s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } } .bar { position: absolute; left: 0; bottom: 0; height: 3px; width: 0; background: ${theme.primaryColor}; transition: width ${POLL_MS}ms linear; pointer-events: none; } .wrap.small button { height: 32px; padding: 0 12px 0 9px; gap: 6px; font-size: 13px; } .wrap.small .more { padding: 0 8px; } .wrap.small .icon, .wrap.small .logo { width: 18px; height: 18px; } @media (prefers-reduced-motion: reduce) { .spinner { animation-duration: 2s; } .bar { transition: none; } } `;
}

class ListenButton {
  readonly host: Element;
  private main: HTMLButtonElement;
  private icon: HTMLElement;
  private label: HTMLElement;
  private bar: HTMLElement;
  private shown: PageListenState | null = null;
  /** data-action="toolbar": the button opens the toolbar and shows no playback state */
  private opensToolbar: boolean;

  constructor(host: Element, theme: ResolvedTheme, private owner: ListenButtons) {
    this.host = host;
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    const small = host.getAttribute('data-size') === 'small';
    this.opensToolbar = host.getAttribute('data-action') === 'toolbar' && !!owner.widget.openToolbar;
    const withToolbar = !this.opensToolbar && host.getAttribute('data-toolbar') === 'true' && !!owner.widget.openToolbar;
    root.innerHTML = `<style>${css(theme)}</style> <div class="wrap${small ? ' small' : ''}"> <button type="button" class="main"><span class="icon"></span><span class="label"></span></button> ${withToolbar ? `<button type="button" class="more" aria-label="${t('Open reading toolbar')}" title="${t('Open reading toolbar')}">${MORE}</button>` : ''} <span class="bar"></span> </div>`;
    this.main = root.querySelector('.main') as HTMLButtonElement;
    this.icon = root.querySelector('.icon') as HTMLElement;
    this.label = root.querySelector('.label') as HTMLElement;
    this.bar = root.querySelector('.bar') as HTMLElement;
    this.main.addEventListener('click', () => {
      if (this.opensToolbar) owner.widget.openToolbar?.();
      else owner.press();
    });
    if (this.opensToolbar) this.main.setAttribute('aria-haspopup', 'true');
    root.querySelector('.more')?.addEventListener('click', () => owner.widget.openToolbar?.());
    host.setAttribute('data-readingvox-ready', '');
  }

  render(state: PageListenState, progress: number): void {
    if (this.opensToolbar) { state = 'idle'; progress = 0; }
    this.bar.style.width = state === 'idle' ? '0' : `${Math.max(0, Math.min(100, progress))}%`;
    if (state === this.shown) return;
    this.shown = state;
    const idle = this.host.getAttribute('data-label') || t('Listen to this page');
    const text = { idle, loading: t('Loading…'), playing: t('Pause'), paused: t('Resume') }[state];
    this.icon.innerHTML = { idle: LOGO, loading: SPINNER, playing: PAUSE, paused: PLAY }[state];
    this.label.textContent = text;
    this.main.setAttribute('aria-label', state === 'idle' ? idle : `${text} – ${idle}`);
    this.main.setAttribute('aria-busy', String(state === 'loading'));
  }

  destroy(): void {
    this.host.shadowRoot?.replaceChildren();
    this.host.removeAttribute('data-readingvox-ready');
  }
}

export class ListenButtons {
  private buttons = new Map<Element, ListenButton>();
  private observer: MutationObserver | null = null;
  private timer: number | null = null;
  /** Clicked, waiting for audio to start */
  private pendingSince = 0;
  private scanQueued = false;

  constructor(readonly widget: ListenButtonHost, private theme: ResolvedTheme) {}

  start(): void {
    this.scan();
    if (typeof MutationObserver !== 'undefined' && document.body) {
      this.observer = new MutationObserver(() => {
        if (this.scanQueued) return;
        this.scanQueued = true;
        requestAnimationFrame(() => { this.scanQueued = false; this.scan(); });
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  /** Main button pressed: pause, or read the page (resuming if paused) */
  press(): void {
    const { state } = this.widget.pageState();
    if (state === 'playing') {
      this.widget.pause();
    } else if (this.pendingSince) {
      return; // already starting
    } else {
      this.pendingSince = Date.now();
      this.widget.playPage().catch(() => { /* errors surface through onError */ })
        .finally(() => { if (this.widget.pageState().state === 'idle') this.pendingSince = 0; this.update(); });
    }
    this.update();
  }

  update = (): void => {
    const current = this.widget.pageState();
    const progress = current.progress;
    let state = current.state;
    if (this.pendingSince) {
      if (state === 'playing' && progress > 0) this.pendingSince = 0;
      else if (state === 'paused' || Date.now() - this.pendingSince > PENDING_MS) this.pendingSince = 0;
      else state = 'loading';
    }
    this.buttons.forEach((b) => b.render(state, progress));
  };

  destroy(): void {
    this.observer?.disconnect();
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    this.buttons.forEach((b) => b.destroy());
    this.buttons.clear();
  }

  private scan(): void {
    this.buttons.forEach((b, el) => {
      if (!el.isConnected) { b.destroy(); this.buttons.delete(el); }
    });
    document.querySelectorAll(SELECTOR).forEach((el) => {
      if (this.buttons.has(el) || (el.shadowRoot && !el.hasAttribute('data-readingvox-ready') && el.shadowRoot.childNodes.length)) return;
      try {
        this.buttons.set(el, new ListenButton(el, this.theme, this));
      } catch { /* element can't host a shadow root (e.g. <button>): skip it */ }
    });
    if (this.buttons.size && this.timer === null) this.timer = window.setInterval(this.update, POLL_MS);
    if (!this.buttons.size && this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
    this.update();
  }
}
